import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { acceptInvitation } from '../services/auth';
import { useAppStore } from '../store/useAppStore';

type Status = 'form' | 'submitting' | 'error' | 'existing_account';

export default function AcceptInvitation() {
  const navigate = useNavigate();
  const { login } = useAppStore();
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') ?? '';
  const email = params.get('email') ?? '';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<Status>(token ? 'form' : 'error');
  const [errorMsg, setErrorMsg] = useState(token ? '' : 'This invitation link is invalid.');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) { setErrorMsg('Please enter your first and last name.'); return; }
    if (password.length < 8) { setErrorMsg('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setErrorMsg('Passwords do not match.'); return; }

    setErrorMsg('');
    setStatus('submitting');
    try {
      const authResult = await acceptInvitation({
        token,
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      if (authResult) {
        login(authResult.token, authResult.role, authResult.profileId, authResult.firstName, authResult.displayName, authResult.email, authResult.orgId, authResult.orgName);
        navigate('/', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        setStatus('existing_account');
      } else if (status === 410) {
        setStatus('error');
        setErrorMsg('This invitation has expired. Ask your admin to resend it.');
      } else {
        setStatus('form');
        setErrorMsg(err?.response?.data?.title ?? 'Could not accept this invitation. Please try again.');
      }
    }
  }

  return (
    <div style={s.outer}>
      <div style={s.card}>
        <Brand />

        {status === 'existing_account' && (
          <>
            <div style={s.icon}>👋</div>
            <h1 style={s.heading}>You already have an account</h1>
            <p style={s.sub}>Sign in with <strong>{email}</strong>, then open this invitation link again to accept it.</p>
            <button style={s.btn} onClick={() => navigate('/login')}>Go to sign in</button>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={s.icon}>⚠️</div>
            <h1 style={s.heading}>Invitation problem</h1>
            <p style={s.sub}>{errorMsg}</p>
            <button style={s.textBtn} onClick={() => navigate('/login')}>Back to sign in</button>
          </>
        )}

        {(status === 'form' || status === 'submitting') && (
          <>
            <h1 style={s.heading}>Join the team</h1>
            <p style={s.sub}>
              {email ? <>Create your account for <strong>{email}</strong> to get started.</> : 'Create your account to get started.'}
            </p>

            <form onSubmit={handleSubmit} style={{ textAlign: 'left', marginTop: 20 }}>
              <div style={{ display: 'flex', gap: 10 }}>
                <input style={s.input} placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                <input style={s.input} placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
              <input style={{ ...s.input, marginTop: 10 }} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
              <input style={{ ...s.input, marginTop: 10 }} type="password" placeholder="Confirm password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />

              {errorMsg && <p style={s.error}>{errorMsg}</p>}

              <button style={{ ...s.btn, marginTop: 16, opacity: status === 'submitting' ? 0.7 : 1 }} type="submit" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Creating your account…' : 'Accept invitation'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function Brand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--accent, #4F46E5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="9" cy="7" r="4" stroke="white" strokeWidth="2"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>
      <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink, #111)', letterSpacing: '-0.3px' }}>SK People</span>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  outer:   { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg, #f5f5f7)', padding: 16 },
  card:    { background: 'var(--surface, #fff)', borderRadius: 16, padding: '40px 36px', width: '100%', maxWidth: 400, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center' },
  icon:    { fontSize: 44, marginBottom: 16 },
  heading: { fontSize: 24, fontWeight: 700, color: 'var(--ink, #111)', margin: '0 0 8px' },
  sub:     { fontSize: 14, color: 'var(--ink-3, #888)', margin: '0 0 4px', lineHeight: 1.7 },
  error:   { fontSize: 13, color: '#dc2626', margin: '12px 0 0' },
  input:   { flex: 1, width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border, #e5e7eb)', fontSize: 14, boxSizing: 'border-box' },
  btn:     { width: '100%', padding: '11px 0', borderRadius: 8, background: 'var(--accent, #4F46E5)', color: '#fff', fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer', marginTop: 20 },
  textBtn: { display: 'block', width: '100%', marginTop: 14, background: 'none', border: 'none', color: 'var(--ink-3, #888)', fontSize: 13, cursor: 'pointer' },
};
